import { useActiveCircle } from '@/hooks/use-active-circle';
import { useIsCircleInitializing } from '@/hooks/use-is-circle-initializing';
import { createHasPermission } from '@calm/evexia-web-common/dist/circle';
import { useState } from 'react';

export interface ConciergeGrants {
    canCreateConcierge: boolean;
    canDeleteConcierge: boolean;
}

interface SettledGrants extends ConciergeGrants {
    circleId: number;
}

export function getConciergeGrants(
    hasPermission: ReturnType<typeof createHasPermission>
): ConciergeGrants {
    const canRead = hasPermission('ai_concierge', 'r');
    return {
        /* Covers both onboarding and updating a concierge plan; create without read is ignored so onboarding and the plan page pick the same flow */
        canCreateConcierge: canRead && hasPermission('ai_concierge', 'c'),
        canDeleteConcierge: canRead && hasPermission('ai_concierge', 'd'),
    };
}

/**
 * The active circle's `ai_concierge` permissions, read once so every concierge
 * consumer agrees on both the grants and on when they are final.
 *
 * Once a circle has settled, its last settled grants are held while the query
 * refreshes or its JWT is masked, so consumers do not fall back to loading. A
 * different circle waits for its own first settle. The hold is per hook
 * instance: one mounted while the circle is unsettled has nothing to hold.
 *
 * `isPermissionsSettled` is true for a held value too, so it is the gate for
 * what to show. `isPermissionsLive` is false while grants are held, so it is
 * the gate for actions that must not run on a masked circle JWT.
 */
export function useConciergePermissions() {
    const {
        activeCircleId,
        hasPermission,
        isPermissionsSettled: isCircleSettled,
        isEnabled,
    } = useActiveCircle();
    const isCircleInitializing = useIsCircleInitializing();
    const [settled, setSettled] = useState<SettledGrants>();

    const live = getConciergeGrants(hasPermission);
    /* A disabled circle query is final at "no permissions" unless its circle can still arrive */
    const isLiveSettled = isCircleSettled && !isCircleInitializing;

    /* A disabled query's settle reads as "no permissions", which is not a value to hold */
    if (!isEnabled) {
        if (settled) {
            setSettled(undefined);
        }
    } else if (
        isLiveSettled &&
        (!settled ||
            settled.circleId !== activeCircleId ||
            settled.canCreateConcierge !== live.canCreateConcierge ||
            settled.canDeleteConcierge !== live.canDeleteConcierge)
    ) {
        setSettled({ circleId: activeCircleId, ...live });
    }

    const held = settled?.circleId === activeCircleId ? settled : undefined;
    const grants = isLiveSettled ? live : held ?? live;

    return {
        canCreateConcierge: grants.canCreateConcierge,
        canDeleteConcierge: grants.canDeleteConcierge,
        isPermissionsSettled: isLiveSettled || !!held,
        isPermissionsLive: isLiveSettled,
    };
}
